import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import React from 'react';

import { dateRangeMedWithZoneOffset } from '../../../helpers/dates';
import { IContentfulImage } from '../../../interfaces/contentful-image.interface';
import { IBloackAppDownloadPanel } from '../../../queries/fragments/blockAppDownloadPanel';
import { ILibraryOpeningHours } from '../../../queries/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '../../../queries/fragments/libraryOpeningHoursException';
import { BlockWrapper } from '../../block-wrapper/block-wrapper';
import { BlockSections } from '../../blocks/block-sections';
import { RenderBlocks } from '../../blocks/render-blocks';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import { RichText } from '../../rich-text/rich-text';
import { Share } from '../../share/share';
import MobileAppLinks from '../mobile-app-links/mobile-app-links';
import { PageComponentMainBottomBorder } from './page-component-main-bottom-border';
import { PageComponentMainHeader } from './page-component-main-header';

interface MainBlock {
  header: string;
  caption?: string;
  typeTag?: string;
  typeTagClass?: string;
  appDownloadPanelReference?: IBloackAppDownloadPanel;
  panelclasses?: string;
  validFrom?: string;
  validTo?: string;
  node_locale: string;
  showSocialShare: boolean;
  logo?: IContentfulImage;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tags?: string[];
}
interface MainBlockRichText extends MainBlock {
  blocks?: BlockSections[];
  richText: RenderRichTextData<ContentfulRichTextGatsbyReference>;
}
interface MainBlockBlocks extends MainBlock {
  richText?: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  blocks: BlockSections[];
}

export const PageComponentMain = ({
  header,
  caption,
  typeTag,
  typeTagClass,
  appDownloadPanelReference,
  panelclasses,
  richText,
  blocks,
  validFrom,
  validTo,
  node_locale,
  showSocialShare,
  logo,
  meetingPlaceOpeningHours,
  tags,
}: MainBlockRichText | MainBlockBlocks) => {
  return (
    <BlockWrapper mode="box" className="pb-0 lg:mb-5 lg:pb-20">
      <PageComponentMainBottomBorder />
      {logo && (
        <ImageWrapper
          gatsbyImageData={logo?.gatsbyImageData}
          file={logo?.file}
          svg={logo?.svg}
          alt={logo?.description ? logo.description : ''}
          className="h-12 w-12 lg:h-16 lg:w-16 mb-8 rounded-md bg-brand-white"
          imgClassName="!object-contain"
        />
      )}
      <PageComponentMainHeader header={header} caption={caption} />
      {typeTag && (
        <div
          className={`mt-2 font-title text-headings font-bold ${typeTagClass} text-text-sticker-blue`}
        >
          {typeTag}
        </div>
      )}
      {validTo && validFrom && (
        <div className="my-6 text-headings text-neutral-grey-700">
          {dateRangeMedWithZoneOffset(validFrom, validTo, node_locale)}
        </div>
      )}
      {tags && (
        <div className="mb-4">
          {tags?.map((tag, index) => {
            return (
              <span
                key={index}
                className="border-2 border-neutral-grey-300 py-1 px-2 mr-2 rounded-md text-headingxs text-neutral-grey-700"
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}
      {appDownloadPanelReference && (
        <MobileAppLinks
          downloadPanel={appDownloadPanelReference}
          panelclasses={panelclasses}
        />
      )}
      {richText && <RichText richText={richText} />}
      {blocks && (
        <RenderBlocks
          blocks={blocks}
          node_locale={node_locale}
          meetingPlaceOpeningHours={meetingPlaceOpeningHours}
        />
      )}
      {showSocialShare && (
        <Share className="absolute -bottom-2 right-0 lg:bottom-8 lg:right-20 xl:right-24" />
      )}
    </BlockWrapper>
  );
};
